import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Monitor, ArrowRight, Info } from "lucide-react";

const VisitorManagement = ({ onBack }) => {
  const navigate = useNavigate();
  const [showRedirectDialog, setShowRedirectDialog] = useState(false);

  // Auto-show the redirect dialog on page load
  useEffect(() => {
    setShowRedirectDialog(true);
  }, []);

  const handleGoToBioCV = () => {
    setShowRedirectDialog(false);
    navigate('/bio-cvsecurity-config');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Access Control
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <img src="/zkteco-logo.png" alt="ZKTeco" className="h-24 w-auto" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            Visitor Management
          </h1>
          <p className="text-lg text-slate-600">
            Software-based visitor management solution
          </p>
        </div>

        {/* Single Card - Software Visitor Management */}
        <div className="max-w-md mx-auto">
          <button
            onClick={() => setShowRedirectDialog(true)}
            className="w-full group"
            data-testid="software-visitor-card"
          >
            <Card className="border-2 border-slate-200 hover:border-blue-500 transition-all duration-300 h-56">
              <CardContent className="p-0 h-full">
                <div className="h-full bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 flex flex-col items-center justify-center text-white relative overflow-hidden">
                  {/* Icon */}
                  <div className="relative z-10 mb-4">
                    <div className="w-16 h-16 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Monitor className="h-8 w-8" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h3 className="relative z-10 text-xl font-bold mb-1 text-center px-4">Software Visitor Management</h3>
                  <p className="relative z-10 text-sm text-white/80 text-center px-4">(Cloud-based visitor management)</p>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>

        {/* Redirect Dialog */}
        <Dialog open={showRedirectDialog} onOpenChange={setShowRedirectDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Info className="h-5 w-5" />
                Software Visitor Management
              </DialogTitle>
              <DialogDescription>
                Visitor Management is now part of the BioCV Security configurator
              </DialogDescription>
            </DialogHeader>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-slate-700 leading-relaxed">
                Software-based Visitor Management has been integrated into the <strong>BioCV Security</strong> configurator as an advanced functionality module.
              </p>
              <p className="text-slate-700 mt-3">
                This integration allows you to combine Access Control, Time & Attendance, and Visitor Management into a single, comprehensive quote.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowRedirectDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleGoToBioCV} className="bg-blue-600 hover:bg-blue-700">
                <span>Go to BioCV Configurator</span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default VisitorManagement;
